import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#ensureAnimationRunning();
    this.setAttribute('data-ready', 'true');

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /**
   * Wait for layout so mobile width measurements are non-zero.
   */
  #waitForLayout() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  /**
   * @returns {Promise<{ numberOfCopies: number, isHorizontalResize: boolean }>}
   */
  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    if (!marqueeItems[0]) {
      return { numberOfCopies: 1, isHorizontalResize: true };
    }

    await this.#waitForLayout();

    let marqueeWidth = this.clientWidth;
    let marqueeItemsWidth = marqueeItems[0].getBoundingClientRect().width;

    // Mobile layout/fonts can report 0 on first paint — retry briefly.
    if (marqueeWidth === 0 || marqueeItemsWidth === 0) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      marqueeWidth = this.clientWidth;
      marqueeItemsWidth = marqueeItems[0].getBoundingClientRect().width;
    }

    const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
    this.#marqueeWidth = marqueeWidth;

    const numberOfCopies =
      marqueeItemsWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeItemsWidth));

    return { numberOfCopies, isHorizontalResize };
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const copies = Math.max(1, numberOfCopies);
    const speed = Math.sqrt(copies) * speedFactor;

    return Math.max(15, speed);
  }

  #ensureAnimationRunning() {
    const animation = this.refs.wrapper.getAnimations()[0];
    if (animation) {
      animation.playbackRate = 1;
      animation.play();
    }
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(numberOfCopies);

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
    this.#ensureAnimationRunning();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
